import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";
import {chatApi} from "../services/ChatService";
import {useMe} from "./useGetUser";
import {userRoleList} from "../utils/constants";
import {showToastModal} from "../jsx/components/shared/ToastModalSwal";
import {socket} from "../socket/socket";

export const useChatRoom = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {mutate} = useMutation({
    mutationFn: chatApi.createRoom,

    onSuccess: (data) => {
      // Assuming the API response includes the new room's ID
      const newRoomId = data?.id; // Adjust this based on the structure of the returned data
      queryClient.invalidateQueries({queryKey: ["chatRoom"]});

      if (newRoomId) {
        navigate(`/messages#${newRoomId}`); // Navigate to the specific chat room
      } else {
        showToastModal({
          title: "Erreur ",
          message: "Une erreur est survenue. Veuillez réessayer.",
          buttonTitle: "Ressayer",
        });
      }
    },

    onError: (error) => {
      showToastModal({
        title: "Erreur ",
        message: "Une erreur est survenue. Veuillez réessayer",
        buttonTitle: "Ressayer",
      });
    },

    // Show a loading toast when the mutation starts
    onMutate: () => {},
  });

  const {mutate: deleteRoom} = useMutation({
    mutationFn: chatApi.deleteRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
    },
  });
  const {mutate: deleteMessage} = useMutation({
    mutationFn: chatApi.deleteMessage,
    onSuccess: () => {
      socket.emit("refetch_messages", "");
    },
  });

  const onDeleteRoom = ({id}) => {
    deleteRoom({id});
  };

  const onStartNewChat = ({clientId, offerId}) => {
    mutate({clientId, offerId});
  };
  const onStartDirectChat = ({clientId, technicianId}) => {
    mutate({clientId, technicianId});
  };
  const onDeleteMessage = ({messageId}) => {
    deleteMessage({messageId});
  };
  return {onStartNewChat, onStartDirectChat, onDeleteRoom, onDeleteMessage};
};

export const useChatRooms = (props, {enabled = true} = {}) => {
  const {data: user, isLoading: isLoadingUser} = useMe();
  const filter =
    user?.selectedRole === userRoleList.CLIENT
      ? {clientId: user?.Client?.id}
      : {technicianId: user?.Technician?.id};

  const {data, isLoading, isError, error} = useQuery({
    queryKey: ["chatRooms", user?.id, user?.selectedRole, props],
    queryFn: () => chatApi.getAll({filter: {...filter, ...props}}),
    enabled: enabled && !!user && !isLoadingUser,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => data.rows,
  });

  return {
    data: data,
    isLoading,
    isError,
    error,
  };
};

export const useGetRoom = ({roomID}) => {
  const {data, isLoading, isError, error} = useQuery({
    queryFn: () => chatApi.getOne({roomID}),
    queryKey: ["chatRoom"],

    select: (data) => data,
  });

  return {
    data: data,
    isLoading,
    isError,
    error,
  };
};
